import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120lSchMService } from './f1120l-sch-m.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-f1120l-sch-m',
    templateUrl: './f1120l-sch-m.component.html',
    styleUrls: ['./f1120l-sch-m.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120lSchMComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Modal properties
  modalTitle = "Schedule M  Other Information (see instructions)";
  showFormLoader = true;
  crudLoading = false;

  // Tab management
  activeTab = 1;
  tabs = [
    { id: 1, name: 'Form Entry' },
    { id: 2, name: 'Form View' },
    { id: 3, name: 'Audit' },
    { id: 4, name: 'GOLD', disabled: true }
  ];

  // Entity data properties
  entityList: any[] = [];
  leId: any;
  cdr_no: any;
  leType: any;
  formName = '1120L';
  hgt = '96%';
  systemFlag = 'D';

  // Form data
  scheduleMFormView: any = { rows: [] };
  scheduleMFormView1: any[] = [];
  scheduleMFormView2: any[] = [];
  formListOriginal: any = {};
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };

  // Batch data for Audit tab
  BatchList: any[] = [];
  BatchListData: any[] = [];
  batchLoader = false;
  showBatchData = false;
  showRequestLoader = true;
  prev_index: any = null;

  // GOLD data
  GoldData: any[] = [];

  // Flag to prevent multiple initialization
  private isInitialized = false;

  // Context from workspace services
  rowData: any = {};
  filterParams: any = {};
  baseUrls: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120lSchMService: F1120lSchMService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      console.log('init() called', {
        hasGlobalService: !!this.globalService,
        hasGENERAL_CONFIG: !!this.GENERAL_CONFIG,
        hasRowData: !!(this.rowData && Object.keys(this.rowData).length > 0),
        hasUSER_SETTINGS: !!this.USER_SETTINGS,
        isInitialized: this.isInitialized
      });

      if (!this.globalService || !this.GENERAL_CONFIG || !this.USER_SETTINGS) {
        console.log('Missing required data, skipping init');
        return;
      }

      if (this.rowData && Object.keys(this.rowData).length > 0 && !this.isInitialized) {
        console.log('Initializing F1120L-SCH-M component');
        this.isInitialized = true;
        this.setupEntityData();
        this.fetchSchMrecords();
        this.fetchSchMimports();
        this.fetchGoldData();
      }
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // MANDATORY ORDER: Tax Year, LEID, LE Name, LE Type, ME
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME },
      { key: "LE Type", value: this.rowData.LE_TYPE },
      { key: "ME", value: this.rowData.ME }
    ];
  }

  fetchSchMrecords() {
    const params = {
      "action_id": 603,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    console.log('fetchSchMrecords called with params:', params);

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('Form data received:', data);
      this.processFormData(data);
      // Don't set showFormLoader here - it's set in processFormData
    }, (error: any) => {
      console.error('Error loading form data:', error);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    });
  }

  fetchSchMimports() {
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120L Sch M",
      "status": 'Y'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=wwledr',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.BatchList = data.jsonObject || [];
      this.BatchList.forEach((value: any, key: number) => {
        this.BatchList[key].TOGGLE = false;
      });
      this.showRequestLoader = false;
      this.cdr.detectChanges();
    }, (error: any) => {
      console.error('Error loading batch data:', error);
      this.showRequestLoader = false;
      this.BatchList = [];
      this.cdr.detectChanges();
    });
  }

  fetchGoldData() {
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120L Sch M",
      "status": 'Y'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=n887j5',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.GoldData = data.jsonObject || [];
      this.cdr.markForCheck();
    }, (error: any) => {
      console.error('Error loading GOLD data:', error);
      this.GoldData = [];
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    console.log('processFormData called, data.irsForm.formItemList.length:', data.irsForm?.formItemList?.length);
    this.scheduleMFormView.rows = [];
    var saveParamDtls: any[] = [];
    var tempDataObj: any = {};

    for (var i = 1; i < data.irsForm.formItemList.length; i++) {
      saveParamDtls = [];
      tempDataObj = {};
      saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');

      for (var j = 0; j < data.irsForm.formItemList[i].dataRowList[0].dataCellList.length; j++) {
        if (j == 0) {
          tempDataObj.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
        } else if (j == 1) {
          tempDataObj.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].name;
        } else if (j == 2) {
          tempDataObj.lineAttrKey = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_a = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          
          const cellValue = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
          
          if (cellValue == null) {
            // NULL = 0 (same as AngularJS line 72)
            tempDataObj.value = 0;
            if (tempDataObj.num == "1b" || tempDataObj.num == "6" || tempDataObj.num == "15") {
              tempDataObj.conditionalField = true;
            } else {
              tempDataObj.conditionalField = false;
            }
          } else if (typeof cellValue === 'string' && cellValue.toLowerCase() === "true") {
            // "true" = true for checkboxes (same as AngularJS line 79)
            tempDataObj.value = true;
            if (tempDataObj.num == "1b" || tempDataObj.num == "6" || tempDataObj.num == "15") {
              tempDataObj.conditionalField = false;
            }
          } else if (typeof cellValue === 'string' && cellValue.toLowerCase() === "false") {
            // "false" = false for checkboxes (same as AngularJS line 84)
            tempDataObj.value = false;
            if (tempDataObj.num == "1b" || tempDataObj.num == "6" || tempDataObj.num == "15") {
              tempDataObj.conditionalField = true;
            }
          } else if (cellValue == 'NA' || cellValue === 'na') {
            // NA value (same as AngularJS line 89)
            tempDataObj.value = 'NA';
            if (tempDataObj.num == "1b" || tempDataObj.num == "6" || tempDataObj.num == "15") {
              tempDataObj.conditionalField = true;
            } else {
              tempDataObj.conditionalField = false;
            }
          } else {
            // For text fields and numbers, keep original value (same as AngularJS line 96)
            tempDataObj.value = cellValue;
          }
        } else if (j == 3) {
          tempDataObj.lineAttrKey2 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name2 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_b = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.value_user_input = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
        } else if (j == 4) {
          tempDataObj.lineAttrKey3 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name3 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_c = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.value_user_input3 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
        } else {
          tempDataObj.lineAttrKey4 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name4 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_d = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].key.split('~')[2];
          tempDataObj.value_user_input4 = data.irsForm.formItemList[i].dataRowList[0].dataCellList[j].value;
        }
      }
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      this.scheduleMFormView.rows.push(tempDataObj);
    }

    // Apply conditional field logic after loading data
    if (this.scheduleMFormView.rows[15] && this.scheduleMFormView.rows[15].value == true) {
      this.toggleConditionalField([16, 17, 18], false);
    } else {
      this.toggleConditionalField([16, 17, 18], true);
    }
    
    if (this.scheduleMFormView.rows[20] && this.scheduleMFormView.rows[20].value == true) {
      this.toggleConditionalField([21, 22], false);
    } else {
      this.toggleConditionalField([21, 22], true);
    }
    
    if (this.scheduleMFormView.rows[23] && this.scheduleMFormView.rows[23].value == true) {
      this.toggleConditionalField([24, 25, 26], false);
    } else {
      this.toggleConditionalField([24, 25, 26], true);
    }
    
    if (this.scheduleMFormView.rows[40] && this.scheduleMFormView.rows[40].value == true) {
      this.toggleConditionalField([40], false);
    } else {
      this.toggleConditionalField([40], true);
    }

    this.formListOriginal = _.cloneDeep(this.scheduleMFormView);
    this.initFormView(this.scheduleMFormView);
    
    // CRITICAL: Turn off loader AFTER all data is processed
    this.showFormLoader = false;
    
    console.log('Form data loaded. scheduleMFormView1 length:', this.scheduleMFormView1.length);
    console.log('Form data loaded. scheduleMFormView2 length:', this.scheduleMFormView2.length);
    console.log('showFormLoader set to FALSE:', this.showFormLoader);
    console.log('Sample row from scheduleMFormView1:', this.scheduleMFormView1[0]);
    console.log('Sample row from scheduleMFormView2:', this.scheduleMFormView2[0]);
    
    // Force change detection
    this.cdr.detectChanges();
  }

  toggleConditionalField(arr: number[], flag: boolean) {
    arr.forEach((value: number) => {
      if (this.scheduleMFormView.rows[value]) {
        this.scheduleMFormView.rows[value].conditionalField = flag;
        if (flag) {
          this.scheduleMFormView.rows[value].value_user_input = "";
        }
      }
    });
  }

  loadTransactions(index: number, trans_key: string) {
    this.batchLoader = true;
    const params = {
      "client_key": this.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120L Sch M",
      "transaction_key": trans_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=l6gzle',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      var tempData = data.jsonObject || [];
      var currLineNo: any;
      var tempBatchList: any = {};
      var tempBatchData: any[] = [];
      
      tempData.forEach((value: any, key: number) => {
        if (key == 0) {
          currLineNo = tempData[key].LINE_NO;
          tempBatchList = tempData[key];
        } else {
          if (tempData[key].LINE_NO == currLineNo) {
            tempBatchList.ATTRIBUTE_VALUE_B = tempData[key].ATTRIBUTE_VALUE;
            tempBatchData.push(tempBatchList);
            tempBatchList = {};
          } else {
            currLineNo = tempData[key].LINE_NO;
            tempBatchData.push(tempBatchList);
            tempBatchList = {};
            tempBatchList = tempData[key];
          }
        }
      });
      
      this.batchLoader = false;
      this.showBatchData = true;
      this.BatchListData = tempBatchData;
      this.cdr.markForCheck();
    }, (error: any) => {
      console.error('Error loading transaction data:', error);
      this.batchLoader = false;
      this.showBatchData = false;
      this.cdr.markForCheck();
    });
  }

  showUploads(index: number, trans_key: string) {
    if (this.prev_index !== null) {
      if (this.prev_index !== index) {
        this.BatchList[this.prev_index].TOGGLE = false;
        this.prev_index = index;
        this.showBatchData = false;
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.loadTransactions(index, trans_key);
      } else {
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.showBatchData = true;
      }
    } else {
      this.prev_index = index;
      this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
      this.loadTransactions(index, trans_key);
    }
  }

  changeTab(tabId: number) {
    // If leaving GOLD tab (4), disable it again
    if (this.activeTab === 4 && tabId !== 4) {
      this.tabs[3].disabled = true;
    }
    
    this.activeTab = tabId;
    this.cdr.detectChanges();
  }

  goldNavigation() {
    console.log('goldNavigation called, enabling GOLD tab');
    this.activeTab = 4;
    this.tabs[3].disabled = false;
  }

  fieldVisibility(index: number, row: any, name?: string) {
    const state = row.value;

    if (index == 1) {
      if (this.scheduleMFormView.rows[index].value) {
        this.scheduleMFormView.rows[2].conditionalField = true;
        this.scheduleMFormView.rows[2].value = false;
        this.scheduleMFormView.rows[2].value_user_input = "";
      }
    } else if (index == 2) {
      if (this.scheduleMFormView.rows[index].value) {
        this.scheduleMFormView.rows[2].conditionalField = false;
        this.scheduleMFormView.rows[1].value = false;
      } else {
        this.scheduleMFormView.rows[2].conditionalField = !this.scheduleMFormView.rows[2].conditionalField;
      }
    } else if (index == 6 || index == 7) {
      if (index == 6 && this.scheduleMFormView.rows[index].value) {
        this.scheduleMFormView.rows[7].value = false;
      } else {
        this.scheduleMFormView.rows[6].value = false;
      }
    } else if (index == 9 || index == 10) {
      if (index == 9 && this.scheduleMFormView.rows[index].value) {
        this.scheduleMFormView.rows[10].value = false;
      } else {
        this.scheduleMFormView.rows[9].value = false;
      }
    } else if (index == 15) {
      this.checkBoxToggle(index, state, [16, 17, 18], "value", "attrib_name");
    } else if (index == 19) {
      this.checkBoxToggle(index, state, [index], "value_user_input", "attrib_name2");
      this.scheduleMFormView.rows[index].value_user_input3 = "";
      this.scheduleMFormView.rows[index].value_user_input4 = "";
    } else if (index == 35) {
      this.checkBoxToggle(index, state, [index], "value_user_input", "attrib_name2");
    } else if (index == 20) {
      this.checkBoxToggle(index, state, [21, 22], "value", "attrib_name");
    } else if (index == 23) {
      this.checkBoxToggle(index, state, [24, 25, 26], "value", "attrib_name");
    } else if (index == 40) {
      this.checkBoxToggle(index, state, [index], "value_user_input", "attrib_name2");
    } else {
      this.scheduleMFormView.rows[index].conditionalField = !this.scheduleMFormView.rows[index].conditionalField;
    }
  }

  checkBoxToggle(index: number, state: boolean, arr: number[], val: string, attr: string) {
    // Toggle conditional field based on state
    this.toggleCondition(arr, state, val, attr);
  }

  toggleCondition(index: number[], state: boolean, type: string, attr: string) {
    index.forEach((value: number) => {
      if ((this.scheduleMFormView.rows[value].conditionalField && !state) || 
          (!this.scheduleMFormView.rows[value].conditionalField && state)) {
        return;
      } else {
        this.scheduleMFormView.rows[value].conditionalField = !this.scheduleMFormView.rows[value].conditionalField;
      }
    });
    
    if (state) {
      index.forEach((value: number) => {
        this.scheduleMFormView.rows[value][type] = this.GoldData[0] ? this.GoldData[0][this.scheduleMFormView.rows[value][attr]] : '';
      });
    } else {
      index.forEach((value: number) => {
        this.scheduleMFormView.rows[value][type] = "";
      });
    }
  }

  onCheckboxChange(index: number, row: any) {
    this.fieldVisibility(index, row);
  }

  onYesNoChange(index: number, value: boolean, row: any) {
    row.value = value;
    this.fieldVisibility(index, row);
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    var formRowDataA: any = {};
    var formRowDataB: any = {};
    var formRowDataC: any = {};
    var formRowDataD: any = {};
    var tempArray: any[] = [];

    for (var i = 0; i < this.scheduleMFormView.rows.length; i++) {
      formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: this.scheduleMFormView.rows[i].saveParams[1],
        line_order: this.scheduleMFormView.rows[i].line_order,
        attrib_order: this.scheduleMFormView.rows[i].attrib_order_a,
        line_description: this.scheduleMFormView.rows[i].description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: this.scheduleMFormView.rows[i].saveParams[2],
        line_no: this.scheduleMFormView.rows[i].num,
        occurence: this.scheduleMFormView.rows[i].saveParams[7],
        line_attrib_key: this.scheduleMFormView.rows[i].lineAttrKey,
        Attribute_Name: this.scheduleMFormView.rows[i].attrib_name,
        Attribute_Value: this.scheduleMFormView.rows[i].value ? this.scheduleMFormView.rows[i].value.toString() : this.scheduleMFormView.rows[i].value === false ? this.scheduleMFormView.rows[i].value.toString() : '',
        trans_dtls_key: this.scheduleMFormView.rows[i].saveParams[3],
        trans_status: this.scheduleMFormView.rows[i].saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataA);
      
      if (typeof this.scheduleMFormView.rows[i].value_user_input !== "undefined") {
        formRowDataB = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleMFormView.rows[i].saveParams[1],
          line_order: this.scheduleMFormView.rows[i].line_order,
          attrib_order: this.scheduleMFormView.rows[i].attrib_order_b,
          line_description: this.scheduleMFormView.rows[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleMFormView.rows[i].saveParams[2],
          line_no: this.scheduleMFormView.rows[i].num,
          occurence: this.scheduleMFormView.rows[i].saveParams[7],
          line_attrib_key: this.scheduleMFormView.rows[i].lineAttrKey2,
          Attribute_Name: this.scheduleMFormView.rows[i].attrib_name2,
          Attribute_Value: this.scheduleMFormView.rows[i].value_user_input ? this.scheduleMFormView.rows[i].value_user_input.toString() : '0',
          trans_dtls_key: this.scheduleMFormView.rows[i].saveParams[3],
          trans_status: this.scheduleMFormView.rows[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
      
      if (typeof this.scheduleMFormView.rows[i].value_user_input3 !== "undefined") {
        formRowDataC = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleMFormView.rows[i].saveParams[1],
          line_order: this.scheduleMFormView.rows[i].line_order,
          attrib_order: this.scheduleMFormView.rows[i].attrib_order_c,
          line_description: this.scheduleMFormView.rows[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleMFormView.rows[i].saveParams[2],
          line_no: this.scheduleMFormView.rows[i].num,
          occurence: this.scheduleMFormView.rows[i].saveParams[7],
          line_attrib_key: this.scheduleMFormView.rows[i].lineAttrKey3,
          Attribute_Name: this.scheduleMFormView.rows[i].attrib_name3,
          Attribute_Value: this.scheduleMFormView.rows[i].value_user_input3 ? this.scheduleMFormView.rows[i].value_user_input3.toString() : '',
          trans_dtls_key: this.scheduleMFormView.rows[i].saveParams[3],
          trans_status: this.scheduleMFormView.rows[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataC);
      }
      
      if (typeof this.scheduleMFormView.rows[i].value_user_input4 !== "undefined") {
        formRowDataD = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleMFormView.rows[i].saveParams[1],
          line_order: this.scheduleMFormView.rows[i].line_order,
          attrib_order: this.scheduleMFormView.rows[i].attrib_order_d,
          line_description: this.scheduleMFormView.rows[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleMFormView.rows[i].saveParams[2],
          line_no: this.scheduleMFormView.rows[i].num,
          occurence: this.scheduleMFormView.rows[i].saveParams[7],
          line_attrib_key: this.scheduleMFormView.rows[i].lineAttrKey4,
          Attribute_Name: this.scheduleMFormView.rows[i].attrib_name4,
          Attribute_Value: this.scheduleMFormView.rows[i].value_user_input4 ? this.scheduleMFormView.rows[i].value_user_input4.toString() : '',
          trans_dtls_key: this.scheduleMFormView.rows[i].saveParams[3],
          trans_status: this.scheduleMFormView.rows[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataD);
      }
    }
    
    this.formData.editrows.push(tempArray);

    const mainData: any[] = [];
    mainData.push(this.formData);

    this.f1120lSchMService.saveF1120lSchMForm(
      this.GENERAL_CONFIG.base_url,
      mainData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.scheduleMFormView = _.cloneDeep(this.formListOriginal);
    this.scheduleMFormView1 = _.cloneDeep(this.formListOriginal.rows.slice(0, 23));
    this.scheduleMFormView2 = _.cloneDeep(this.formListOriginal.rows.slice(23));
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  initFormView(filteredFormData: any) {
    this.scheduleMFormView = filteredFormData;
    this.scheduleMFormView1 = this.scheduleMFormView.rows.slice(0, 23);
    this.scheduleMFormView2 = this.scheduleMFormView.rows.slice(23);
    console.log('initFormView called. Total rows:', this.scheduleMFormView.rows.length);
    console.log('scheduleMFormView1:', this.scheduleMFormView1.length);
    console.log('scheduleMFormView2:', this.scheduleMFormView2.length);
    
    // Debug checkbox values
    if (this.scheduleMFormView.rows.length > 0) {
      console.log('First 5 rows with values:');
      for (let i = 0; i < Math.min(5, this.scheduleMFormView.rows.length); i++) {
        console.log(`Row ${i}: num=${this.scheduleMFormView.rows[i].num}, value=${this.scheduleMFormView.rows[i].value} (${typeof this.scheduleMFormView.rows[i].value}), description=${this.scheduleMFormView.rows[i].description?.substring(0, 50)}`);
      }
    }
  }

  // TrackBy function for performance optimization
  itemTracker(index: number, item: any): any {
    return item.num || index;
  }
}

